import { useState } from 'react'
import { normalizedText } from '@domain/product'
import type { Category } from '@domain/finance'
import { EscolhaEmTelaCheia } from '@ui/shell/EscolhaEmTelaCheia'
import { CATEGORY_KINDS } from './labels'

type Props = {
  atual: string | null
  categorias: Category[]
  onEscolher: (categoryId: string | null) => void
  /** Criar uma categoria com este nome e entrar nela. */
  onCriar: (nome: string) => void
  onFechar: () => void
}

/**
 * Escolher a categoria de um produto, em tela cheia — igual à da família.
 *
 * ## O `<select>` nativo tinha um argumento, e ele venceu a própria validade
 *
 * O docblock do `CategoryPicker` defende o seletor do sistema assim: *"São 51
 * categorias em três níveis. Um seletor próprio precisaria de busca, navegação
 * por teclado, foco preso e leitura de tela — e ainda seria pior no celular"*.
 * Era verdade quando foi escrito e deixou de ser por dois motivos: esse seletor
 * próprio **existe** desde a decisão 242, com busca e foco resolvidos, e a
 * árvore de 51 virou a árvore que a casa está montando à mão.
 *
 * O que sobra do argumento continua valendo em outro lugar: dentro de uma
 * LINHA da lista de transações, o `<select>` continua sendo o certo. Ali a
 * escolha é um detalhe de uma linha entre dezenas; aqui é um campo de um
 * formulário, ao lado de outro campo que já abre em tela cheia.
 *
 * ## A hierarquia é o recuo, e a busca a desmonta de propósito
 *
 * Ordenadas por `path`, pai vem antes de filha e o recuo desenha o nível. Ao
 * buscar, o recuo perde o sentido — o pai pode não estar na lista — e o que
 * aparece é o **caminho** da categoria, que é como se sabe qual "Mercado" é
 * qual quando duas árvores têm uma.
 *
 * ## Criar daqui nasce na RAIZ e como despesa
 *
 * `kind` é herdado do pai e não pode divergir (`Category.md`), então criar sem
 * escolher pai exige escolher o tipo — e produto que se compra é despesa. Quem
 * quiser uma subcategoria ou outro tipo tem a tela de Ajustes › Categorias,
 * que é onde a árvore se administra.
 */
export function EscolherCategoria({ atual, categorias, onEscolher, onCriar, onFechar }: Props) {
  const [busca, setBusca] = useState('')

  // A MESMA normalização do catálogo e das outras buscas: "alimentacao" acha
  // "Alimentação", e sem isso o acento decidiria se a categoria aparece.
  const termo = normalizedText(busca)
  const buscando = termo !== ''

  const visiveis = categorias
    // Arquivada não aparece — mas a que já está escolhida, sim: escondê-la
    // faria a próxima escolha apagar em silêncio uma classificação feita de
    // propósito. Mesma regra do `CategoryPicker`.
    .filter((c) => c.archivedAt === null || c.id === atual)
    .filter((c) => !buscando || normalizedText(c.name).includes(termo))
    // `path` é a chave natural da ordem da árvore: ele é o caminho de slugs, e
    // ordenar por ele já é a travessia em profundidade — sem uma segunda
    // travessia que possa discordar de `categoryPath`.
    .sort((a, b) => a.path.join('/').localeCompare(b.path.join('/')))

  const nomeNovo = busca.trim()
  const jaExiste = categorias.some((c) => normalizedText(c.name) === termo)

  return (
    <EscolhaEmTelaCheia
      titulo="Categoria"
      rotulo="Escolher a categoria"
      rotuloDaBusca="Buscar categoria"
      busca={busca}
      onBuscar={setBusca}
      onFechar={onFechar}
      criar={{
        rotulo: nomeNovo === '' || jaExiste ? '+ Criar uma categoria' : `+ Criar “${nomeNovo}”`,
        aoCriar:
          nomeNovo === '' || jaExiste
            ? null
            : () => {
                onCriar(nomeNovo)
              },
      }}
    >
      <ul aria-label="Categorias">
        {/* "Sem categoria" é uma escolha, e não a ausência de uma: tirar a
            categoria de um produto classificado por engano tem de ser possível,
            e é diferente de nunca ter escolhido. Some na busca, como o "Sem
            família" — não é um resultado, é a opção de zerar. */}
        {!buscando && (
          <li>
            <LinhaDeCategoria
              nome="Sem categoria"
              detalhe="fica fora das somas por categoria"
              escolhida={atual === null}
              nivel={0}
              onClick={() => {
                onEscolher(null)
              }}
            />
          </li>
        )}

        {visiveis.map((c) => (
          <li key={c.id}>
            <LinhaDeCategoria
              nome={c.name}
              detalhe={
                buscando && c.path.length > 1
                  ? c.path.join(' / ')
                  : `${CATEGORY_KINDS[c.kind]}${c.archivedAt !== null ? ' · arquivada' : ''}`
              }
              // Buscando, o recuo mente: o pai pode ter ficado de fora do
              // filtro, e a filha apareceria deslocada sem nada acima dela.
              nivel={buscando ? 0 : c.depth}
              escolhida={atual === c.id}
              onClick={() => {
                onEscolher(c.id)
              }}
            />
          </li>
        ))}

        {visiveis.length === 0 && (
          <li className="px-1 py-6 text-center text-sm text-[var(--text-muted)]">
            {`Nenhuma categoria com “${nomeNovo}”. Dá para criar uma abaixo.`}
          </li>
        )}
      </ul>
    </EscolhaEmTelaCheia>
  )
}

/** Uma categoria na lista: o nome, o nível e o que ela é. */
function LinhaDeCategoria({
  nome,
  detalhe,
  nivel,
  escolhida,
  onClick,
}: {
  nome: string
  detalhe: string
  nivel: number
  escolhida: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={escolhida ? 'true' : undefined}
      // O véu da escolhida é da MARCA, e não branco: branco sobre este fundo
      // vira cinza sujo. Mesma escolha da linha de família (decisão 242).
      className={`alvo w-full justify-start gap-2 px-2 text-left text-sm ${
        escolhida
          ? 'rounded-[var(--radius-sm)] bg-[color-mix(in_oklab,var(--brand)_10%,transparent)]'
          : 'border-b border-[var(--border)]'
      }`}
      style={{ paddingLeft: `${String(0.5 + nivel * 0.9)}rem` }}
    >
      {nivel > 0 && (
        <span aria-hidden="true" className="shrink-0 text-[var(--text-faint)]">
          ↳
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{nome}</span>
        <span className="block truncate text-xs text-[var(--text-faint)]">{detalhe}</span>
      </span>
      {escolhida && (
        <span aria-hidden="true" className="shrink-0 text-[var(--economia)]">
          ✓
        </span>
      )}
    </button>
  )
}
